// This is the entry point for the home component
import React, { FC, useCallback, useMemo, useEffect, useContext } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';

// Styling
import injectSheet from 'react-jss';

// Actions

// Components
import MultiSelect from 'Components/MultiSelect';

// Utilities
import { areEqual, areEqualObjects } from 'Utils/areEqual';

import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import {
  setListMembershipValue,
  setNewListValue,
} from './actions/listMembership.actions';
import styleSheet from './styles';
import { allListsGql, findListsByIdsGql } from './gqlQuery';

interface Props {
  classes: any;
}

const ListMembership: FC<Props> = (props: Props) => {
  const { listMembership } = useSelector(
    (selectors: any) => selectors.filter,
    areEqualObjects
  );

  const {
    filterTrackingHelpers: {
      Listmembership: { listLabels },
    },
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  useEffect(() => {
    if (listMembership.lists.length > 0)
      filterTrack({
        filterCategory: 'ListMemberShip',
        filterName: 'List_names',
        filterValue: listLabels(listMembership.lists),
        addToStore: listMembership.lists.length > 0, // we only want to add this to the store if the value is Any.  We will track the slider values in rangeSliderChange
      });
  }, [listMembership.lists.length]);

  const dispatch = useDispatch();

  const { lists, newList } = listMembership;

  const userDetails = useContext(UserDetailsContext);

  const listIds = lists.map((list: any) => list.value);

  const { data: listMembershipData, refetch: refetchAllLists } = useQuery(
    allListsGql,
    {
      variables: {
        instId: userDetails.instId,
        accountId: userDetails.accountId,
        sort: 'name:asc',
        size: 1000,
      },
      fetchPolicy: 'cache-and-network',
    }
  );
  const listMembershipCallback = (event: any) => {
    setListMembershipValue(dispatch, event.target.value);
  };

  const { refetch: refetchSelectedLists } = useQuery(findListsByIdsGql, {
    variables: {
      instId: userDetails.instId,
      accountId: userDetails.accountId,
      size: 1000,
      listIds,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data: any) => {
      if (data) {
        const {
          paginatedList: { list: value },
        } = data;
        listMembershipCallback({
          target: { value },
        });
      }
    },
    skip: !(listIds.length > 0),
  });

  // This useEffect checks periodically to make sure all names
  // are up to date
  useEffect(() => {
    refetchAllLists();
    if (listIds.length > 0) {
      refetchSelectedLists();
    }
    setNewListValue(dispatch, false);
  }, [newList]);

  const { classes } = props;

  const onDropdownChangeListMembership = (event: any) => {
    let newSelected = [...lists];
    if (lists.some((option: any) => areEqual(event.target.value, option))) {
      newSelected = newSelected.filter(
        (option) => !areEqual(event.target.value, option)
      );
    } else {
      newSelected.push(event.target.value);
    }

    const response = { target: { value: newSelected, id: 'value' } };
    listMembershipCallback(response);
  };

  const memoizedListMembershipLists = useMemo(
    () => (!listMembershipData ? [] : listMembershipData.paginatedList.lists),
    [listMembershipData]
  );

  return (
    <div className={classes.container}>
      <MultiSelect
        selectedOptions={lists}
        items={memoizedListMembershipLists}
        onDropdownChange={useCallback(onDropdownChangeListMembership, [
          listMembership,
        ])}
        selectionDescription={(lists.length > 1 && 'Lists') || 'List'}
        placeholder="Search List"
      />
    </div>
  );
};

export default withErrorBoundary(injectSheet(styleSheet)(ListMembership));
